<script setup>
import { computed } from 'vue';
import { Handle, Position } from '@vue-flow/core';

const props = defineProps({
  data: { type: Object, required: true },
  selected: { type: Boolean, default: false },
});

const kind = computed(() => props.data.kind);
const isStart = computed(() => kind.value === 'start');
const isEnd = computed(() => kind.value === 'end');
const isCondition = computed(() => kind.value === 'condition');
const isSwitch = computed(() => kind.value === 'switch');

const themeClasses = computed(() => {
  const themes = {
    start: 'border-emerald-300 bg-emerald-50',
    send_text: 'border-sky-300 bg-sky-50',
    wait: 'border-amber-300 bg-amber-50',
    condition: 'border-teal-300 bg-teal-50',
    switch: 'border-indigo-300 bg-indigo-50',
    capture_input: 'border-blue-300 bg-blue-50',
    assign_agent: 'border-cyan-300 bg-cyan-50',
    set_status: 'border-violet-300 bg-violet-50',
    set_labels: 'border-rose-300 bg-rose-50',
    http_request: 'border-lime-300 bg-lime-50',
    ai_agent: 'border-fuchsia-300 bg-fuchsia-50',
    end: 'border-slate-300 bg-slate-50',
  };

  return themes[kind.value] || 'border-n-weak bg-n-solid-1';
});

const iconClasses = computed(() => {
  const themes = {
    start: 'i-lucide-play text-emerald-700',
    send_text: 'i-lucide-message-square text-sky-700',
    wait: 'i-lucide-clock-3 text-amber-700',
    condition: 'i-lucide-git-branch text-teal-700',
    switch: 'i-lucide-git-fork text-indigo-700',
    capture_input: 'i-lucide-message-circle-question text-blue-700',
    assign_agent: 'i-lucide-user-check text-cyan-700',
    set_status: 'i-lucide-circle-dot text-violet-700',
    set_labels: 'i-lucide-tags text-rose-700',
    http_request: 'i-lucide-globe text-lime-700',
    ai_agent: 'i-lucide-sparkles text-fuchsia-700',
    end: 'i-lucide-octagon text-slate-600',
  };

  return themes[kind.value] || 'i-lucide-circle';
});

const switchCases = computed(() => props.data.config?.cases || []);
const switchHandlePositions = computed(() => {
  const handleCount = switchCases.value.length + 1;
  if (handleCount === 1) return [50];

  return Array.from(
    { length: handleCount },
    (_, index) => 25 + (index * 50) / (handleCount - 1)
  );
});
</script>

<template>
  <div
    class="relative min-w-[13.75rem] max-w-[17.5rem] rounded-2xl border-2 px-4 py-3 shadow-sm transition-shadow"
    :class="[themeClasses, selected ? 'ring-2 ring-n-brand shadow-lg' : '']"
  >
    <Handle
      v-if="!isStart"
      type="target"
      :position="Position.Left"
      class="!h-3 !w-3 !border-2 !border-white !bg-n-brand"
    />

    <div class="flex items-start gap-3">
      <span
        class="flex items-center justify-center flex-none w-9 h-9 rounded-xl bg-white/80"
      >
        <span class="text-lg" :class="iconClasses" />
      </span>
      <div class="min-w-0">
        <p class="text-sm font-semibold text-n-slate-12">
          {{ data.label }}
        </p>
        <p v-if="data.summary" class="mt-0.5 text-xs leading-5 text-n-slate-11">
          {{ data.summary }}
        </p>
      </div>
    </div>

    <Handle
      v-if="!isEnd && !isCondition && !isSwitch"
      type="source"
      :position="Position.Right"
      class="!h-3 !w-3 !border-2 !border-white !bg-n-brand"
    />

    <template v-if="isCondition">
      <Handle
        id="yes"
        type="source"
        :position="Position.Right"
        class="!top-[35%] !h-3 !w-3 !border-2 !border-white !bg-emerald-600"
      />
      <span
        class="absolute -translate-y-1/2 right-4 top-[35%] text-[0.625rem] font-medium text-emerald-700"
      >
        {{ data.yesLabel }}
      </span>
      <Handle
        id="no"
        type="source"
        :position="Position.Right"
        class="!top-[70%] !h-3 !w-3 !border-2 !border-white !bg-rose-500"
      />
      <span
        class="absolute -translate-y-1/2 right-4 top-[70%] text-[0.625rem] font-medium text-rose-600"
      >
        {{ data.noLabel }}
      </span>
    </template>

    <template v-if="isSwitch">
      <Handle
        v-for="(flowCase, index) in switchCases"
        :id="flowCase.branch"
        :key="flowCase.branch"
        type="source"
        :position="Position.Right"
        :style="{ top: `${switchHandlePositions[index]}%` }"
        class="!h-3 !w-3 !border-2 !border-white !bg-indigo-600"
      />
      <Handle
        id="default"
        type="source"
        :position="Position.Right"
        :style="{ top: `${switchHandlePositions[switchCases.length]}%` }"
        class="!h-3 !w-3 !border-2 !border-white !bg-slate-500"
      />
    </template>
  </div>
</template>
